import React, { useState, useEffect } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI } from '../../shared/styles';
import GroupDetailScreen from './GroupDetailScreen';

const BASE = process.env.REACT_APP_API_URL || '/api';

const ICONS  = ['🧩','🧘','⚡','🎯','💪','🌿','🏃','🧠','🔥','🌊','📚','🎵'];
const COLORS = ['#C8A84B','#7B6FD0','#2A9E82','#D4873A','#C06090','#5B9ED0'];

function FocusGroupsTab({ lang, currentUser, onNav }) {
  const nl  = lang === 'nl';
  const uid = currentUser?.id;
  const [view, setView] = useState('list');
  const [selGroupId, setSelGroupId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newG, setNewG] = useState({ name: '', description: '', icon: '🧩', color: '#C8A84B', isPublic: true });

  const loadGroups = () => {
    if (!uid) return;
    setLoading(true);
    fetch(`${BASE}/groups?uid=${encodeURIComponent(uid)}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setGroups(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadGroups(); }, [uid]);

  const createGroup = async () => {
    if (!newG.name.trim() || !uid) return;
    setCreating(true);
    try {
      const r = await fetch(`${BASE}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newG, uid }),
      });
      if (r.ok) {
        const d = await r.json();
        setNewG({ name: '', description: '', icon: '🧩', color: '#C8A84B', isPublic: true });
        setShowCreate(false);
        if (d.id) { setSelGroupId(d.id); setView('detail'); }
        else loadGroups();
      }
    } catch {} finally { setCreating(false); }
  };

  if (view === 'detail' && selGroupId) {
    return (
      <GroupDetailScreen
        groupId={selGroupId}
        currentUserId={uid}
        userName={currentUser?.name || 'You'}
        lang={lang}
        onNav={onNav}
        onBack={() => { setView('list'); setSelGroupId(null); loadGroups(); }}
      />
    );
  }

  const myGroups      = groups.filter(g => g.myStatus === 'accepted');
  const pendingInvites = groups.filter(g => g.myStatus === 'invited');
  const pendingJoins  = groups.filter(g => g.myStatus === 'pending');
  const discover      = groups.filter(g => !g.myStatus && g.isPublic);

  return (
    <div className="fadeUp">
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ color: C.white, fontSize: 15, fontWeight: 600 }}>{nl ? 'Groepen' : 'Groups'}</div>
        <button style={s_PB({ padding: '8px 16px', fontSize: 13 })} onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? '✕' : '+ ' + (nl ? 'Aanmaken' : 'Create')}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div style={s_GC({ marginBottom: 14 })}>
          <div style={{ fontSize: 12, color: _T.accent, letterSpacing: 2, fontWeight: 700, marginBottom: 10 }}>{nl ? 'NIEUWE GROEP' : 'NEW GROUP'}</div>
          <input value={newG.name} onChange={e => setNewG({ ...newG, name: e.target.value })}
            placeholder={nl ? 'Naam *' : 'Name *'} style={s_TI({ marginBottom: 8, padding: '10px 12px' })} />
          <textarea value={newG.description} onChange={e => setNewG({ ...newG, description: e.target.value })}
            placeholder={nl ? 'Beschrijving...' : 'Description...'} style={s_TI({ minHeight: 60, resize: 'none', marginBottom: 8, padding: '10px 12px' })} />
          <div style={{ marginBottom: 8 }}>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 5 }}>{nl ? 'Icoon' : 'Icon'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {ICONS.map(ic => (
                <button key={ic} onClick={() => setNewG({ ...newG, icon: ic })}
                  style={{ background: newG.icon === ic ? `${_T.accent}22` : 'transparent', border: `1px solid ${newG.icon === ic ? _T.accent : _T.accent + '33'}`, borderRadius: 8, padding: '4px 8px', fontSize: 16, cursor: 'pointer' }}>
                  {ic}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 5 }}>{nl ? 'Kleur' : 'Color'}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {COLORS.map(col => (
                <button key={col} onClick={() => setNewG({ ...newG, color: col })}
                  style={{ width: 26, height: 26, borderRadius: '50%', background: col, border: `2px solid ${newG.color === col ? C.white : 'transparent'}`, cursor: 'pointer' }} />
              ))}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.silver, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={newG.isPublic} onChange={e => setNewG({ ...newG, isPublic: e.target.checked })} />
            {nl ? 'Publieke groep (iedereen kan aanvragen)' : 'Public group (anyone can request to join)'}
          </label>
          <button style={s_PB({ width: '100%', opacity: newG.name.trim() && !creating ? 1 : 0.4 })}
            onClick={createGroup} disabled={!newG.name.trim() || creating}>
            {creating ? '...' : (nl ? 'Aanmaken' : 'Create group')}
          </button>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', color: C.silver, padding: 24 }}>...</div>}

      {/* Invitations */}
      {!loading && pendingInvites.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>
            📨 {nl ? 'UITNODIGINGEN' : 'INVITATIONS'} ({pendingInvites.length})
          </div>
          {pendingInvites.map(g => (
            <GroupCard key={g.id} g={g} badge="invited" nl={nl} onClick={() => { setSelGroupId(g.id); setView('detail'); }} />
          ))}
        </div>
      )}

      {/* Pending join requests */}
      {!loading && pendingJoins.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>
            ⏳ {nl ? 'AANVRAAG INGEDIEND' : 'REQUESTED'}
          </div>
          {pendingJoins.map(g => (
            <GroupCard key={g.id} g={g} badge="pending" nl={nl} onClick={() => { setSelGroupId(g.id); setView('detail'); }} />
          ))}
        </div>
      )}

      {/* My groups */}
      {!loading && myGroups.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>{nl ? 'MIJN GROEPEN' : 'MY GROUPS'}</div>
          {myGroups.map(g => (
            <GroupCard key={g.id} g={g} nl={nl} onClick={() => { setSelGroupId(g.id); setView('detail'); }} />
          ))}
        </div>
      )}

      {!loading && myGroups.length === 0 && !showCreate && pendingInvites.length === 0 && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 24 }), color: C.silver, fontSize: 13 }}>
          {nl ? 'Nog geen groepen. Maak er een aan of ontdek er een!' : 'No groups yet. Create one or discover one!'}
        </div>
      )}

      {/* Discover */}
      {!loading && discover.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>{nl ? 'ONTDEK' : 'DISCOVER'}</div>
          {discover.map(g => (
            <GroupCard key={g.id} g={g} nl={nl} onClick={() => { setSelGroupId(g.id); setView('detail'); }} />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupCard({ g, badge, nl, onClick }) {
  return (
    <div onClick={onClick} style={{ ...s_GC({ cursor: 'pointer', padding: 14, marginBottom: 8 }) }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ width: 46, height: 46, borderRadius: 13, background: `${g.color}18`, border: `2px solid ${g.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
          {g.icon || '🧩'}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{g.name}</div>
          {g.description && <div style={{ color: C.silver, fontSize: 11, lineHeight: 1.4 }}>{g.description.slice(0, 60)}{g.description.length > 60 ? '…' : ''}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 11, color: C.silver }}>👥 {g.memberCount || 0}</span>
            {g.myRole === 'admin' && <span style={{ fontSize: 9, color: _T.accent, fontWeight: 700 }}>👑 ADMIN</span>}
            {badge === 'invited' && <span style={{ fontSize: 9, color: C.orange || '#D4873A', fontWeight: 700 }}>📨 {nl ? 'UITGENODIGD' : 'INVITED'}</span>}
            {badge === 'pending' && <span style={{ fontSize: 9, color: C.silver, fontWeight: 700 }}>⏳ {nl ? 'IN BEHANDELING' : 'PENDING'}</span>}
          </div>
        </div>
        <div style={{ color: C.silver, fontSize: 18 }}>›</div>
      </div>
    </div>
  );
}

export default FocusGroupsTab;
